import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, BookOpen, ChevronRight, PartyPopper, LayoutGrid, List, GalleryHorizontal, ChevronUp, ChevronDown, Library, Mic, ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import LegacyWritingStudio from './LegacyWritingStudio';
import { ImmersiveStoryGallery } from './ImmersiveStoryGallery';
import { ArchiveSubjectIdentity, firstGivenPossessiveLabel } from '../archive/ArchiveSubjectIdentity';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { personHref } from '../../utils/prettyArchiveLinks';
import { getApiBase } from '../../utils/apiBase';
import { LoadingState } from '../ui/LoadingState';
import { Card, CardContent } from '../ui/card';
import { useOnboarding } from '../onboarding';
import { consumePendingStartForMe } from '../../utils/startForMeStorage';
import { START_FOR_ME_COPY } from '../../content/memoryPromptLibrary';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';
import { CORE_EMPTY_STATES } from '../../content/coreJourneyContent';

const FIRST_MEMORY_CELEBRATION_KEY = 'gb_onboarding_first_memory_celebration';

const API = getApiBase();

const INDEX_STATUS_STYLES = {
  polished: 'border-[hsl(88_18%_82%)]/90 bg-[hsl(88_24%_96%)] text-[hsl(88_18%_28%)]',
  draft: 'border-[hsl(38_14%_86%)] bg-[hsl(42_22%_97%)] text-[hsl(32_10%_38%)]',
  voice: 'border-[hsl(38_22%_84%)]/90 bg-[hsl(42_28%_99%)] text-[#5c4a32]',
  new: 'border-[hsl(38_14%_88%)] bg-[hsl(42_26%_99%)] text-muted-foreground',
};

function getStoryVoiceNoteCount(story) {
  const segments = story?.audio_segments;
  if (Array.isArray(segments) && segments.length > 0) return segments.length;
  return story?.audio_file_url ? 1 : 0;
}

function getStoryIndexStatus(story) {
  const hasPolished = Boolean((story?.polished_transcript || '').trim());
  const hasText = Boolean((story?.raw_transcript || '').trim());
  const voiceCount = getStoryVoiceNoteCount(story);
  if (hasPolished) return { label: 'Polished', tone: 'polished' };
  if (voiceCount > 0 && !hasText) return { label: 'Voice only', tone: 'voice' };
  if (hasText) return { label: 'Draft', tone: 'draft' };
  return { label: 'New memory', tone: 'new' };
}

export const StoriesTab = ({
  personId,
  treeId = null,
  canEdit = false,
  subject = null,
  mentionedStories = [],
  subjectIdentityAttachment = true,
  initialStoryId = null,
  /** Person page: open the list/editor workspace (same path as ?story= deep links). */
  editorFirst = false,
  /** e.g. "voice" — scroll voice/memory UI into view after the editor loads. */
  initialFocus = null,
}) => {
  const { markFirstStoryRecorded, hasRecordedFirstStory } = useOnboarding();
  const [celebrationDismissed, setCelebrationDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(FIRST_MEMORY_CELEBRATION_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [starterQuestions, setStarterQuestions] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);
  
  // Question selection state
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [customQuestion, setCustomQuestion] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [creating, setCreating] = useState(false);
  const [storyDisplayTitle, setStoryDisplayTitle] = useState('');
  const [startForMeRecordHint, setStartForMeRecordHint] = useState(null);
  const initialStoryAppliedRef = useRef(false);
  const editorFirstAppliedRef = useRef(false);
  /** Tile cards for scanning; list opens the memory workspace + editor (default on person page). */
  const [storyViewMode, setStoryViewMode] = useState(() => {
    if (editorFirst) return 'list';
    try {
      const v = localStorage.getItem('gb_story_view_mode');
      if (v === 'list' || v === 'gallery' || v === 'tiles') return v;
      return 'tiles';
    } catch {
      return 'tiles';
    }
  });

  const persistStoryViewMode = (mode) => {
    const next = mode === 'list' || mode === 'gallery' || mode === 'tiles' ? mode : 'tiles';
    setStoryViewMode(next);
    try {
      localStorage.setItem('gb_story_view_mode', next);
    } catch {
      /* ignore */
    }
  };

  const openStoryFromGallery = useCallback((index) => {
    setActiveStoryIndex(index);
    setStoryViewMode('list');
    try {
      localStorage.setItem('gb_story_view_mode', 'list');
    } catch {
      /* ignore */
    }
  }, []);

  const storyPrimaryTitle = (s) => {
    const t = (s.display_title || '').trim();
    if (t) return t;
    return (s.question_text || '').trim() || 'Story';
  };

  const storyPickerLabel = (s) => {
    const raw = storyPrimaryTitle(s);
    return raw.length > 48 ? `${raw.slice(0, 46)}…` : raw;
  };

  const fetchStories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/individuals/${personId}/stories`);
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  const fetchChapters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/story-chapters`);
      setChapters(response.data.chapters || []);
      setStarterQuestions(response.data.starter_questions || []);
    } catch (error) {
      console.error('Error fetching chapters:', error);
    }
  }, []);

  useEffect(() => {
    fetchStories();
    fetchChapters();
  }, [fetchStories, fetchChapters]);

  useEffect(() => {
    const pending = consumePendingStartForMe();
    if (!pending || pending.personId !== personId) return;
    if (pending.action === 'write') {
      setUseCustom(true);
      setCustomQuestion(pending.mainPrompt || '');
      setShowCreateDialog(true);
    } else if (pending.action === 'record') {
      setStartForMeRecordHint(pending);
      setStoryViewMode('list');
    }
  }, [personId]);

  useEffect(() => {
    if (!initialStoryId || stories.length === 0 || initialStoryAppliedRef.current) return;
    const idx = stories.findIndex((s) => s.id === initialStoryId);
    if (idx >= 0) {
      initialStoryAppliedRef.current = true;
      setActiveStoryIndex(idx);
      setStoryViewMode('list');
    }
  }, [initialStoryId, stories]);

  useEffect(() => {
    if (!editorFirst || stories.length === 0 || editorFirstAppliedRef.current) return;
    editorFirstAppliedRef.current = true;
    setStoryViewMode('list');
    if (!initialStoryId) {
      setActiveStoryIndex(0);
    }
  }, [editorFirst, stories.length, initialStoryId]);

  useEffect(() => {
    if (initialFocus !== 'voice' || storyViewMode !== 'list' || loading) return;
    const timer = window.setTimeout(() => {
      const target =
        document.querySelector('[data-testid="latest-voice-playback-strip"]') ||
        document.querySelector('[data-testid="read-listen-card"]') ||
        document.querySelector('[data-testid="story-voice-history-rail"]') ||
        document.querySelector('[data-testid="read-earlier-voice-notes"]');
      target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [initialFocus, storyViewMode, loading, activeStoryIndex, stories.length]);

  const handleCreateStory = async () => {
    let questionText = '';
    let chapterId = null;
    let questionId = null;
    let isCustom = false;
    
    if (useCustom) {
      if (!customQuestion.trim()) {
        toast.error('Please enter a question');
        return;
      }
      questionText = customQuestion.trim();
      isCustom = true;
      chapterId = selectedChapter || null;
    } else {
      if (!selectedQuestion) {
        toast.error('Please select a question');
        return;
      }
      for (const chapter of chapters) {
        const question = chapter.questions.find(q => q.id === selectedQuestion);
        if (question) {
          questionText = question.text;
          chapterId = chapter.id;
          questionId = question.id;
          break;
        }
      }
    }
    
    setCreating(true);
    try {
      const titleTrim = storyDisplayTitle.trim();
      const response = await axios.post(`${API}/individuals/${personId}/stories`, {
        person_id: personId,
        chapter_id: chapterId,
        question_id: questionId,
        question_text: questionText,
        display_title: titleTrim || null,
        is_custom_question: isCustom
      });
      
      setStories([response.data, ...stories]);
      setActiveStoryIndex(0); // Focus on the new story
      setShowCreateDialog(false);
      resetDialogState();
      setStoryDisplayTitle('');
      markFirstStoryRecorded();
      toast.success('Story saved — your card is ready.');
    } catch (error) {
      console.error('Error creating story:', error);
      toast.error('Failed to create story card');
    } finally {
      setCreating(false);
    }
  };

  const resetDialogState = () => {
    setSelectedChapter('');
    setSelectedQuestion('');
    setCustomQuestion('');
    setUseCustom(false);
    setStoryDisplayTitle('');
  };

  const handleStoryUpdate = () => {
    fetchStories();
  };

  const handleStoryDelete = (storyId) => {
    const newStories = stories.filter(s => s.id !== storyId);
    setStories(newStories);
    if (activeStoryIndex >= newStories.length) {
      setActiveStoryIndex(Math.max(0, newStories.length - 1));
    }
  };

  const handleNavigateChapter = (direction) => {
    if (direction === 'prev' && activeStoryIndex > 0) {
      setActiveStoryIndex(activeStoryIndex - 1);
    } else if (direction === 'next' && activeStoryIndex < stories.length - 1) {
      setActiveStoryIndex(activeStoryIndex + 1);
    }
  };

  // Get questions for selected chapter
  const getChapterQuestions = () => {
    if (!selectedChapter) return [];
    const chapter = chapters.find(c => c.id === selectedChapter);
    if (!chapter) return [];
    
    const usedQuestionIds = stories
      .filter(s => s.question_id)
      .map(s => s.question_id);
    
    return chapter.questions.filter(q => !usedQuestionIds.includes(q.id));
  };

  const selectStarterQuestion = (starter) => {
    const chapterId = starter.chapter_id || 'chapter-1';
    setSelectedChapter(chapterId);
    const chapter = chapters.find((c) => c.id === chapterId);
    const match = chapter?.questions?.find((q) => q.id === starter.id);
    if (match) {
      setUseCustom(false);
      setSelectedQuestion(match.id);
      setCustomQuestion('');
    } else {
      setUseCustom(true);
      setCustomQuestion(starter.text);
      setSelectedQuestion('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingState message="Loading stories..." variant="minimal" />
      </div>
    );
  }

  const activeStory = stories[activeStoryIndex];
  const canMoveEarlier = Boolean(activeStory) && activeStoryIndex > 0;
  const canMoveLater = Boolean(activeStory) && activeStoryIndex < stories.length - 1;

  const reorderActiveStory = async (direction) => {
    const story = stories[activeStoryIndex];
    if (!story?.id) return;
    if (direction === 'earlier' && activeStoryIndex <= 0) return;
    if (direction === 'later' && activeStoryIndex >= stories.length - 1) return;
    const storyId = String(story.id).trim();
    try {
      const { data } = await axios.post(`${API}/stories/${encodeURIComponent(storyId)}/reorder`, { direction });
      if (Array.isArray(data)) {
        setStories(data);
        const nextIdx = data.findIndex((s) => s && String(s.id).toLowerCase() === storyId.toLowerCase());
        if (nextIdx >= 0) setActiveStoryIndex(nextIdx);
      } else {
        await fetchStories();
      }
      toast.success('Story order updated');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const d = typeof detail === 'string' ? detail : '';
      if (status === 404 || d === 'Not Found' || /not found/i.test(d)) {
        toast.error('Could not update story order');
      } else {
        toast.error(d || 'Could not update story order');
      }
    }
  };

  const showFirstMemoryCelebration =
    hasRecordedFirstStory && stories.length > 0 && !celebrationDismissed;

  const dismissCelebration = () => {
    try {
      sessionStorage.setItem(FIRST_MEMORY_CELEBRATION_KEY, '1');
    } catch {
      /* ignore */
    }
    setCelebrationDismissed(true);
  };

  const openCreateWithChapter = (chapterId) => {
    setSelectedChapter(chapterId);
    setSelectedQuestion('');
    setUseCustom(false);
    setShowCreateDialog(true);
  };

  const openCreateForPhoto = () => {
    setStoryDisplayTitle('Photo memory');
    setUseCustom(true);
    setCustomQuestion('A moment captured in a photograph — what do you see, and what does it mean to the family?');
    setShowCreateDialog(true);
  };

  const starterChapters = chapters.slice(0, 4);

  const renderStoryIndexButton = (story, index) => {
    const isActive = index === activeStoryIndex;
    const voiceCount = getStoryVoiceNoteCount(story);
    const status = getStoryIndexStatus(story);
    const hasPromptSubtitle = Boolean((story.display_title || '').trim());

    return (
      <button
        key={story.id}
        type="button"
        onClick={() => setActiveStoryIndex(index)}
        className={`w-full rounded-xl border px-3 py-2.5 text-left transition-all ${
          isActive
            ? 'border-[#8B7355]/40 bg-[hsl(42_28%_100%)] shadow-[inset_3px_0_0_0_#8B7355,0_1px_4px_hsl(28_12%_18%/_0.05)] ring-1 ring-[#c9a961]/25'
            : 'border-transparent hover:border-[hsl(38_14%_80%)]/90 hover:bg-white/70 dark:hover:bg-stone-950/40'
        }`}
        data-testid={`story-index-item-${story.id}`}
        aria-current={isActive ? 'true' : undefined}
      >
        <span className="block font-serif text-sm font-medium leading-snug text-[hsl(24_12%_20%)] line-clamp-2">
          {storyPrimaryTitle(story)}
        </span>
        {hasPromptSubtitle ? (
          <span className="mt-1 block text-[11px] leading-snug text-muted-foreground line-clamp-2">
            {story.question_text}
          </span>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium tracking-wide ${INDEX_STATUS_STYLES[status.tone]}`}
          >
            {status.label}
          </span>
          {voiceCount > 0 ? (
            <span className="inline-flex items-center gap-0.5 rounded-full border border-[hsl(38_14%_86%)] bg-[hsl(42_26%_99%)] px-2 py-0.5 text-[10px] text-[#5c4a32]/90">
              <Mic className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
              {voiceCount} voice {voiceCount === 1 ? 'note' : 'notes'}
            </span>
          ) : null}
        </div>
      </button>
    );
  };

  const renderStoryIndexMoveControls = (className = '') => (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full rounded-full border-[hsl(38_14%_82%)] bg-[hsl(42_28%_100%)]/80"
        disabled={!canMoveEarlier}
        onClick={() => reorderActiveStory('earlier')}
        title={!canMoveEarlier ? 'Already first in the list' : undefined}
      >
        <ChevronUp className="mr-1 h-4 w-4" aria-hidden />
        Move earlier
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full rounded-full border-[hsl(38_14%_82%)] bg-[hsl(42_28%_100%)]/80"
        disabled={!canMoveLater}
        onClick={() => reorderActiveStory('later')}
        title={!canMoveLater ? 'Already last in the list' : undefined}
      >
        Move later
        <ChevronDown className="ml-1 h-4 w-4" aria-hidden />
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="stories-tab">
      {startForMeRecordHint ? (
        <div
          className="rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-[hsl(42_28%_99%)] px-4 py-4 sm:px-5"
          data-testid="start-for-me-record-hint"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b7355]">
            {START_FOR_ME_COPY.label}
          </p>
          <p className="mt-2 font-serif text-base leading-relaxed text-[#1c1917]">
            {startForMeRecordHint.mainPrompt}
          </p>
          <p className="mt-2 text-xs text-[#7a7268]">
            Record below when you are ready — follow-ups are optional.
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 h-8 rounded-full px-2 text-xs text-muted-foreground"
            onClick={() => setStartForMeRecordHint(null)}
          >
            Dismiss
          </Button>
        </div>
      ) : null}
      {mentionedStories.length > 0 && (
        <Card className="border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/90" data-testid="mentioned-stories-card">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <Library className="w-5 h-5 text-[#8B7355] shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-serif font-medium text-foreground">Mentioned in family stories</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  These memories live on another profile; they link here so you can find every thread of the archive.
                </p>
                <ul className="mt-4 space-y-2">
                  {mentionedStories.map((m) => (
                    <li key={m.story_id}>
                      <Link
                        to={`${personHref({ id: m.story_subject_person_id, slug: m.story_subject_slug })}?view=stories`}
                        className="group flex flex-col rounded-lg border border-transparent px-2 py-2 hover:border-[hsl(38_14%_78%)] hover:bg-background/60"
                      >
                        <span className="font-serif text-sm font-medium text-foreground group-hover:underline">
                          {m.memory_title}
                        </span>
                        <span className="text-xs text-muted-foreground mt-0.5">
                          On {m.story_subject_display}&apos;s profile
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {showFirstMemoryCelebration && (
        <Card className="border-primary/25 bg-primary/5" data-testid="first-memory-celebration">
          <CardContent className="pt-4 pb-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                <PartyPopper className="w-5 h-5 text-primary" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-serif font-medium text-foreground">Your first story is saved</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You can add more stories anytime, or return to your dashboard to invite family.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button asChild size="sm" className="rounded-full">
                    <Link to="/dashboard" onClick={dismissCelebration}>
                      Continue
                    </Link>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={dismissCelebration}>
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workspace toolbar — person context lives in PersonStudioHeader when editorFirst */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {!editorFirst ? (
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            {subject && subjectIdentityAttachment && (
              <ArchiveSubjectIdentity person={subject} variant="default" className="shrink-0 sm:pt-0.5" />
            )}
            <div className="min-w-0">
              <h3 className="text-lg font-serif font-medium tracking-tight">
                {(() => {
                  const p = firstGivenPossessiveLabel(subject);
                  return p ? `${p} stories` : 'Stories';
                })()}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                A written memory archive — voice notes are optional and sit alongside your words.
              </p>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
              Memory studio
            </p>
            <p className="mt-0.5 font-serif text-base text-foreground/90">
              {(() => {
                const p = firstGivenPossessiveLabel(subject);
                return p ? `${p} stories & voice` : 'Stories & voice';
              })()}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {stories.length > 0 && (
            <div className="flex rounded-full border border-border/50 bg-muted/30 p-0.5">
              <Button
                type="button"
                variant={storyViewMode === 'tiles' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 rounded-full px-3"
                onClick={() => persistStoryViewMode('tiles')}
                aria-pressed={storyViewMode === 'tiles'}
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Tiles</span>
              </Button>
              <Button
                type="button"
                variant={storyViewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 rounded-full px-3"
                onClick={() => persistStoryViewMode('list')}
                aria-pressed={storyViewMode === 'list'}
              >
                <List className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">List</span>
              </Button>
              <Button
                type="button"
                variant={storyViewMode === 'gallery' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 rounded-full px-3"
                onClick={() => persistStoryViewMode('gallery')}
                aria-pressed={storyViewMode === 'gallery'}
              >
                <GalleryHorizontal className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Gallery</span>
              </Button>
            </div>
          )}
          {canEdit && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-full"
              data-testid="add-story-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add story
            </Button>
          )}
        </div>
      </div>
      
      {/* Stories - Using Legacy Writing Studio */}
      {stories.length === 0 ? (
        <div
          className="mt-1 overflow-hidden rounded-[1.5rem] border border-[hsl(38_14%_86%)]/95 bg-gradient-to-br from-[hsl(42_28%_99%)] via-[hsl(40_24%_98%)] to-[hsl(38_22%_96%)] shadow-[inset_0_1px_0_hsl(42_40%_100%_/_0.55),0_12px_40px_hsl(28_12%_18%_/_0.05)]"
          data-testid="stories-empty-state"
        >
          <div className="grid grid-cols-1 gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-10 lg:p-10 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
            <div className="flex min-w-0 flex-col justify-center space-y-5">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[hsl(38_16%_88%)] bg-[hsl(42_26%_99%)] text-[#8b7355] ring-1 ring-[#c9a961]/12">
                <BookOpen className="h-8 w-8" aria-hidden />
              </div>
              <div className="space-y-3">
                <h3 className="font-serif text-2xl font-medium tracking-tight text-[hsl(24_12%_18%)] sm:text-3xl">
                  {CORE_JOURNEY_UI
                    ? CORE_EMPTY_STATES.noMemories.title
                    : (() => {
                        const p = firstGivenPossessiveLabel(subject);
                        return p ? `Begin ${p} memory archive` : 'Begin this memory archive';
                      })()}
                </h3>
                <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-[1.0625rem]">
                  {canEdit
                    ? CORE_JOURNEY_UI
                      ? CORE_EMPTY_STATES.noMemories.description
                      : 'Start with one memory. A voice, a short story, or a single photograph can begin this archive. One day, their voice becomes priceless.'
                    : 'Stories and voice memories will appear here when someone adds them.'}
                </p>
              </div>
              {canEdit ? (
                <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="w-full rounded-full bg-[#1c1917] px-6 text-[#faf7f2] shadow-sm hover:bg-[#2c2824] sm:w-auto"
                    data-testid="start-memory-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" aria-hidden />
                    {CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noMemories.ctaWrite : 'Start a story'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(true)}
                    className="w-full rounded-full border-[hsl(38_14%_82%)] bg-[hsl(42_28%_100%)]/90 text-[#5c4a32] shadow-none hover:bg-[hsl(42_28%_100%)] sm:w-auto"
                    data-testid="record-audio-empty-btn"
                  >
                    <Mic className="mr-2 h-4 w-4 opacity-80" aria-hidden />
                    {CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noMemories.ctaRecord : 'Record voice note'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openCreateForPhoto}
                    className="w-full rounded-full border-[hsl(38_14%_82%)] bg-[hsl(42_28%_100%)]/90 text-[#5c4a32] shadow-none hover:bg-[hsl(42_28%_100%)] sm:w-auto"
                    data-testid="attach-photo-empty-btn"
                  >
                    <ImageIcon className="mr-2 h-4 w-4 opacity-80" aria-hidden />
                    {CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noPhotos.cta : 'Attach photo'}
                  </Button>
                </div>
              ) : null}
              {canEdit && treeId ? (
                <p className="text-xs leading-relaxed text-muted-foreground/90">
                  Need someone else in your archive first?{' '}
                  <Link
                    to={buildTreePeopleAddPersonUrl(treeId)}
                    className="font-medium text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-2 hover:text-[#1c1917]"
                    data-testid="stories-empty-add-person-link"
                  >
                    Add a person
                  </Link>{' '}
                  from People, then return here to capture their voice or words.
                </p>
              ) : null}
            </div>

            {canEdit && starterChapters.length > 0 ? (
              <div className="min-w-0 rounded-2xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_28%_100%)]/50 p-5 sm:p-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Starter chapters
                </p>
                <p className="mt-1 mb-4 text-sm leading-relaxed text-muted-foreground">
                  Pick a chapter to open the story card with a guided prompt.
                </p>
                <ul className="space-y-2.5">
                  {starterChapters.map((chapter) => (
                    <li key={chapter.id}>
                      <button
                        type="button"
                        onClick={() => openCreateWithChapter(chapter.id)}
                        className="group w-full rounded-xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)]/90 px-4 py-3.5 text-left transition hover:border-[#8B7355]/35 hover:bg-white hover:shadow-sm"
                        data-testid={`starter-chapter-${chapter.id}`}
                      >
                        <p className="font-serif text-sm font-medium text-foreground/90 group-hover:text-[#5c4a32]">
                          {chapter.title}
                        </p>
                        {chapter.subtitle ? (
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-2">
                            {chapter.subtitle}
                          </p>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      ) : storyViewMode === 'tiles' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {stories.map((story, index) => (
            <button
              key={story.id}
              type="button"
              onClick={() => {
                setActiveStoryIndex(index);
                persistStoryViewMode('list');
              }}
              className="group rounded-2xl border border-[hsl(38_14%_86%)] bg-gradient-to-b from-[hsl(42_30%_100%)] to-[hsl(38_22%_96%)] p-5 text-left shadow-sm transition hover:border-[hsl(38_14%_72%)] hover:shadow-md"
            >
              {subject && (
                <ArchiveSubjectIdentity person={subject} variant="compact" className="mb-3 opacity-[0.95]" />
              )}
              <p className="font-serif text-base font-medium text-[hsl(24_12%_20%)] line-clamp-2">
                {storyPrimaryTitle(story)}
              </p>
              {(story.display_title || '').trim() ? (
                <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                  <span className="font-medium text-muted-foreground/90">Prompted by · </span>
                  {story.question_text}
                </p>
              ) : null}
              <p className="mt-4 text-xs font-medium text-primary/80 group-hover:underline">Open story</p>
            </button>
          ))}
        </div>
      ) : storyViewMode === 'gallery' ? (
        <ImmersiveStoryGallery
          stories={stories}
          subject={subject}
          activeIndex={activeStoryIndex}
          onActiveIndexChange={setActiveStoryIndex}
          onOpenStory={openStoryFromGallery}
          storyLabel={storyPickerLabel}
        />
      ) : (
        <div
          className="overflow-hidden rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_26%_99%)]/95 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.45),0_4px_24px_hsl(28_12%_18%/_0.04)]"
          data-testid="stories-workspace"
        >
          <div className="border-b border-[hsl(38_14%_86%)]/80 bg-gradient-to-r from-[hsl(42_28%_99%)] to-[hsl(40_22%_97%)] px-4 py-2 sm:px-5 lg:px-6">
            {editorFirst ? (
              <p className="text-xs text-muted-foreground">
                {stories.length > 1
                  ? `${stories.length} memories — pick from the index, write in the editor`
                  : 'Write, record voice, or attach media in the editor below'}
              </p>
            ) : (
              <>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(32_9%_48%)]">
                  Memory workspace
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {stories.length > 1
                    ? 'Pick a story in the index, then write or listen in the editor beside it.'
                    : 'Your story editor opens here — voice notes stay alongside your words.'}
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col lg:flex-row lg:items-stretch">
            {stories.length > 1 && (
              <details className="group border-b border-[hsl(38_14%_86%)]/70 bg-[hsl(42_24%_98%)]/80 lg:hidden">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-[hsl(24_12%_22%)] [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2">
                    <Library className="h-4 w-4 text-[#8B7355]" aria-hidden />
                    Story index
                    <span className="rounded-full border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_100%)] px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
                      {stories.length}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition group-open:rotate-180" aria-hidden />
                </summary>
                <div className="border-t border-[hsl(38_14%_86%)]/60 px-3 pb-3 pt-2">
                  <nav className="space-y-1" aria-label="Story index">
                    {stories.map((story, index) => renderStoryIndexButton(story, index))}
                  </nav>
                  {activeStory && canEdit && renderStoryIndexMoveControls('mt-3 pt-2 border-t border-[hsl(38_14%_86%)]/50')}
                </div>
              </details>
            )}

            {stories.length > 1 && (
              <aside
                className="hidden w-[min(100%,15.5rem)] shrink-0 flex-col border-r border-[hsl(38_14%_86%)]/80 bg-gradient-to-b from-[hsl(42_30%_100%)] to-[hsl(40_22%_97%)] lg:flex xl:w-[16.5rem]"
                aria-label="Story index"
              >
                <div className="border-b border-[hsl(38_14%_86%)]/70 px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[hsl(32_9%_48%)]">
                    Story index
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {stories.length} {stories.length === 1 ? 'memory' : 'memories'}
                  </p>
                </div>
                <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-3 max-h-[min(70vh,36rem)]">
                  {stories.map((story, index) => renderStoryIndexButton(story, index))}
                </nav>
                {activeStory && canEdit && (
                  <div className="border-t border-[hsl(38_14%_86%)]/70 px-3 py-3">
                    {renderStoryIndexMoveControls()}
                    <p className="mt-3 text-[10px] leading-relaxed text-muted-foreground/80">
                      Drag-and-drop reordering may come later — use Move earlier / later for now.
                    </p>
                  </div>
                )}
              </aside>
            )}

              <div className="min-w-0 flex-1 bg-[hsl(42_28%_99%)]/40">
              <div className="border-b border-[hsl(38_14%_86%)]/50 px-4 py-2.5 lg:px-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(32_9%_48%)]">
                  {activeStory ? storyPrimaryTitle(activeStory) : 'Editor'}
                </p>
              </div>
              <div className="p-3 sm:p-4 lg:p-5 xl:px-6">
                {activeStory && canEdit && stories.length > 1 && (
                  <div className="mb-3 flex flex-wrap justify-end gap-2 lg:hidden">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[hsl(38_14%_82%)]"
                      disabled={!canMoveEarlier}
                      onClick={() => reorderActiveStory('earlier')}
                      title={!canMoveEarlier ? 'Already first in the list' : undefined}
                    >
                      <ChevronUp className="mr-1 h-4 w-4" aria-hidden />
                      Move earlier
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[hsl(38_14%_82%)]"
                      disabled={!canMoveLater}
                      onClick={() => reorderActiveStory('later')}
                      title={!canMoveLater ? 'Already last in the list' : undefined}
                    >
                      Move later
                      <ChevronDown className="ml-1 h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                )}

                {activeStory ? (
                  <LegacyWritingStudio
                    key={activeStory.id}
                    story={activeStory}
                    archiveSubject={subject || undefined}
                    canEdit={canEdit}
                    workspaceEmbedded
                    onUpdate={handleStoryUpdate}
                    onDelete={handleStoryDelete}
                    chapterInfo={
                      stories.length > 1
                        ? {
                            current: activeStoryIndex + 1,
                            total: stories.length,
                          }
                        : null
                    }
                    onNavigateChapter={stories.length > 1 ? handleNavigateChapter : null}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Story Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetDialogState();
      }}>
        <DialogContent className="sm:max-w-lg" data-testid="create-story-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">New Story Card</DialogTitle>
            <DialogDescription>
              Start with a gentle prompt, or choose from more chapters when you are ready
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {starterQuestions.length > 0 && !useCustom ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Suggested starters</Label>
                <p className="text-xs text-muted-foreground">
                  A few warm prompts to begin — you can explore more chapters below.
                </p>
                <div className="flex flex-col gap-2">
                  {starterQuestions.slice(0, 6).map((starter) => (
                    <Button
                      key={starter.id}
                      type="button"
                      variant="outline"
                      className="h-auto justify-start whitespace-normal rounded-xl px-3 py-2.5 text-left text-sm font-normal"
                      onClick={() => selectStarterQuestion(starter)}
                      data-testid={`starter-prompt-${starter.id}`}
                    >
                      {starter.text}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Story title (optional)</Label>
              <Input
                value={storyDisplayTitle}
                onChange={(e) => setStoryDisplayTitle(e.target.value)}
                placeholder="e.g. Sunday dinners with Grandma"
                maxLength={120}
                className="text-base"
                data-testid="story-display-title-input"
              />
              <p className="text-xs text-muted-foreground">
                Shown in your archive and lists. The question below still guides the narrative.
              </p>
            </div>
            {!useCustom ? (
              <>
                {/* Chapter Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Select a Chapter</Label>
                  <Select value={selectedChapter} onValueChange={(value) => {
                    setSelectedChapter(value);
                    setSelectedQuestion('');
                  }}>
                    <SelectTrigger data-testid="chapter-select">
                      <SelectValue placeholder="Choose a chapter..." />
                    </SelectTrigger>
                    <SelectContent>
                      {chapters.map((chapter) => (
                        <SelectItem key={chapter.id} value={chapter.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{chapter.title}</span>
                            <span className="text-muted-foreground">— {chapter.subtitle}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Question Selection */}
                {selectedChapter && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Select a Question</Label>
                    <Select value={selectedQuestion} onValueChange={setSelectedQuestion}>
                      <SelectTrigger data-testid="question-select">
                        <SelectValue placeholder="Choose a question..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getChapterQuestions().map((question) => (
                          <SelectItem key={question.id} value={question.id}>
                            <div className="max-w-[350px]">
                              <p className="truncate">{question.text}</p>
                              <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                                {question.type} prompt
                              </p>
                            </div>
                          </SelectItem>
                        ))}
                        {getChapterQuestions().length === 0 && (
                          <div className="px-2 py-3 text-sm text-muted-foreground">
                            All questions in this chapter have been used
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="pt-2">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setUseCustom(true)}
                    className="text-xs px-0 text-muted-foreground hover:text-primary"
                  >
                    <ChevronRight className="w-3 h-3 mr-1" />
                    Or write your own question
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Your Question</Label>
                <Input
                  value={customQuestion}
                  onChange={(e) => setCustomQuestion(e.target.value)}
                  placeholder="What would you like to ask?"
                  className="text-base"
                  data-testid="custom-question-input"
                />
                <p className="text-xs text-muted-foreground">
                  Write a question that feels meaningful to you
                </p>
                
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setUseCustom(false);
                    setCustomQuestion('');
                  }}
                  className="text-xs px-0 text-muted-foreground hover:text-primary"
                >
                  ← Back to guided questions
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetDialogState();
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateStory}
              disabled={creating || (!useCustom && !selectedQuestion) || (useCustom && !customQuestion.trim())}
              className="rounded-full"
              data-testid="create-story-submit-btn"
            >
              {creating ? 'Creating...' : 'Create Story Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoriesTab;
